import { useState } from "react";
import { Lock, Link2, Check, Copy, RotateCcw, Eye } from "lucide-react";
import { Calculadora } from "@/components/Calculadora";
import { DEFAULTS, buildClientUrl } from "@/lib/pricing";

const ADMIN_PASSWORD = "usfq2026";
const SESSION_KEY = "motivy_admin_ok";

const CAMPOS = [
  { key: "precioRRHH", label: "Precio por usuario RRHH (Rango Base)", hint: "Fijo · 1,200 usuarios", prefix: "$", step: 0.01 },
  { key: "precioTramo1", label: "Precio base Rango 1", hint: "Primeros 3,900 estudiantes", prefix: "$", step: 0.01 },
  { key: "precioTramo2", label: "Precio base Rango 2", hint: "Estudiantes 3,901 – 7,800", prefix: "$", step: 0.01 },
  { key: "descuentoTele", label: "Descuento por quitar Salud Mental", hint: "Telepsicología ilimitada · sobre Rango 1 y 2", suffix: "%", step: 0.5 },
  { key: "descuentoNutri", label: "Descuento por quitar Nutrición", hint: "Telenutrición ilimitada · sobre Rango 1 y 2", suffix: "%", step: 0.5 },
  { key: "descuentoModulo", label: "Descuento por quitar Salud Física o Descanso", hint: "Por cada uno de estos módulos · sobre Rango 1 y 2", suffix: "%", step: 0.5 },
];

const CAMPOS_ROI = [
  { key: "roiMargen", label: "Ahorro por encima de la inversión", hint: "El ahorro de este servicio siempre será este % arriba de lo que paga el cliente", suffix: "%", step: 1 },
  { key: "roiCitasAno", label: "Citas promedio al año", hint: "Por persona que usa el servicio", suffix: "citas", step: 0.1 },
  { key: "roiCostoCita", label: "Costo de mercado por cita", hint: "Precio de referencia de una sesión", prefix: "$", step: 1 },
];

const CAMPOS_ROI_NUTRI = [
  { key: "roiMargenNutri", label: "Ahorro por encima de la inversión", hint: "El ahorro de este servicio siempre será este % arriba de lo que paga el cliente", suffix: "%", step: 1 },
  { key: "roiCitasAnoNutri", label: "Consultas promedio al año", hint: "Por persona que usa el servicio", suffix: "citas", step: 0.1 },
  { key: "roiCostoCitaNutri", label: "Costo de mercado por consulta", hint: "Precio de referencia de una consulta", prefix: "$", step: 1 },
];

const CAMPOS_ESCALA = [
  { key: "roiEscala100", label: "Aumento del retorno al 100% de estudiantes", hint: "Cuánto sube el % de retorno al 100% vs 50% (economías de escala)", suffix: "%", step: 5 },
  { key: "statNoUsa", label: "Estadística: no usa por el costo", hint: "% de estudiantes que no usa estos servicios por el costo", suffix: "%", step: 1 },
];

export default function AdminPage() {
  const [authed, setAuthed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "1"
  );

  if (!authed) {
    return <PasswordGate onSuccess={() => setAuthed(true)} />;
  }
  return <AdminPanel />;
}

function PasswordGate({ onSuccess }) {
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (pwd === ADMIN_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, "1");
      onSuccess();
    } else {
      setError(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#24255b] flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm"
        data-testid="admin-gate"
      >
        <div className="w-12 h-12 rounded-xl bg-[#24255b] flex items-center justify-center text-white mb-5">
          <Lock className="w-6 h-6" />
        </div>
        <h1 className="text-xl font-extrabold text-slate-900">Panel de administración</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">
          Ingresa la contraseña para editar los precios base.
        </p>
        <input
          type="password"
          value={pwd}
          onChange={(e) => {
            setPwd(e.target.value);
            setError(false);
          }}
          placeholder="Contraseña"
          autoFocus
          data-testid="admin-password-input"
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#24255b]/30 focus:border-[#24255b]"
        />
        {error && (
          <p className="text-sm text-red-500 mt-2" data-testid="admin-error">
            Contraseña incorrecta.
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-btn"
          className="mt-5 w-full rounded-xl bg-[#24255b] text-white font-semibold py-3 hover:bg-[#24255b]/90 transition-colors"
        >
          Entrar
        </button>
      </form>
    </div>
  );
}

function AdminPanel() {
  const [config, setConfig] = useState(DEFAULTS);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const setCampo = (key, value) => {
    const num = value === "" ? "" : Number(value);
    setConfig((c) => ({ ...c, [key]: value === "" ? "" : num }));
    setGeneratedUrl("");
  };

  const normalizado = () => {
    const clean = { ...DEFAULTS };
    for (const k of Object.keys(DEFAULTS)) {
      const v = config[k];
      clean[k] = v === "" || Number.isNaN(Number(v)) ? DEFAULTS[k] : Number(v);
    }
    return clean;
  };

  const generar = () => {
    const url = buildClientUrl(normalizado());
    setGeneratedUrl(url);
    setCopied(false);
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl);
    } catch (e) {
      /* clipboard puede fallar sin https, se muestra el input igualmente */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetear = () => {
    setConfig(DEFAULTS);
    setGeneratedUrl("");
  };

  return (
    <div className="min-h-screen bg-[#f4f5fa]">
      <header className="bg-[#24255b] text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center font-extrabold text-lg">
              M
            </div>
            <div className="leading-tight">
              <p className="font-extrabold tracking-tight">Motivy · Admin</p>
              <p className="text-[11px] text-white/60 font-medium">Configuración de precios USFQ</p>
            </div>
          </div>
          <span className="text-xs font-medium text-white/60 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> Modo privado
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 space-y-8">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Valores editables */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-7">
            <h2 className="text-lg font-bold text-slate-900">Valores base</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">
              Estos valores nunca se muestran al cliente.
            </p>
            <div className="space-y-5">
              {CAMPOS.map((campo) => (
                <Campo
                  key={campo.key}
                  campo={campo}
                  value={config[campo.key]}
                  onChange={setCampo}
                />
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-[#eff0f6]">
              <h3 className="text-base font-bold text-slate-900">Ahorro / ROI · Telepsicología</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                Usados en la calculadora de ahorro de salud mental.
              </p>
              <div className="space-y-5">
                {CAMPOS_ROI.map((campo) => (
                  <Campo
                    key={campo.key}
                    campo={campo}
                    value={config[campo.key]}
                    onChange={setCampo}
                  />
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[#eff0f6]">
              <h3 className="text-base font-bold text-slate-900">Ahorro / ROI · Telenutrición</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                Usados en la calculadora de ahorro de alimentación.
              </p>
              <div className="space-y-5">
                {CAMPOS_ROI_NUTRI.map((campo) => (
                  <Campo
                    key={campo.key}
                    campo={campo}
                    value={config[campo.key]}
                    onChange={setCampo}
                  />
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[#eff0f6]">
              <h3 className="text-base font-bold text-slate-900">Escala del retorno</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                Ajuste por economías de escala según el número de estudiantes.
              </p>
              <div className="space-y-5">
                {CAMPOS_ESCALA.map((campo) => (
                  <Campo
                    key={campo.key}
                    campo={campo}
                    value={config[campo.key]}
                    onChange={setCampo}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={resetear}
              data-testid="admin-reset-btn"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Restaurar valores por defecto
            </button>
          </div>

          {/* Generar enlace */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-7 h-fit">
            <div className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#24255b]" />
              <h2 className="text-lg font-bold text-slate-900">Enlace para el cliente</h2>
            </div>
            <p className="text-sm text-slate-500 mt-1 mb-5">
              Genera un enlace con los valores actuales. El cliente solo verá los selectores y
              resultados, nunca los precios base ni descuentos.
            </p>

            <button
              onClick={generar}
              data-testid="admin-generate-btn"
              className="w-full rounded-xl bg-[#24255b] text-white font-semibold py-3 hover:bg-[#24255b]/90 transition-colors"
            >
              Generar enlace para cliente
            </button>

            {generatedUrl && (
              <div className="mt-5" data-testid="admin-generated-url-box">
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={generatedUrl}
                    data-testid="admin-generated-url"
                    className="flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-600 bg-slate-50 truncate"
                  />
                  <button
                    onClick={copiar}
                    data-testid="admin-copy-btn"
                    className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 text-white px-4 text-sm font-semibold hover:bg-slate-800 transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <a
                  href={generatedUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="admin-open-link"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#24255b] hover:underline"
                >
                  <Eye className="w-4 h-4" /> Abrir vista del cliente
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Vista previa */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
              Vista previa (lo que verá el cliente)
            </h2>
          </div>
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-4 sm:p-6 bg-white/40">
            <Calculadora config={normalizado()} />
          </div>
        </div>
      </main>
    </div>
  );
}

function Campo({ campo, value, onChange }) {
  return (
    <div data-testid={`admin-field-${campo.key}`}>
      <label className="text-sm font-semibold text-slate-700">{campo.label}</label>
      <p className="text-xs text-slate-400 mb-1.5">{campo.hint}</p>
      <div className="relative">
        {campo.prefix && (
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">
            {campo.prefix}
          </span>
        )}
        <input
          type="number"
          step={campo.step}
          min={0}
          value={value}
          onChange={(e) => onChange(campo.key, e.target.value)}
          data-testid={`admin-input-${campo.key}`}
          className={
            "w-full rounded-xl border border-slate-200 py-2.5 text-sm font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-[#24255b]/30 focus:border-[#24255b] " +
            (campo.prefix ? "pl-8 pr-3.5 " : "px-3.5 ") +
            (campo.suffix ? "pr-16" : "")
          }
        />
        {campo.suffix && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-xs">
            {campo.suffix}
          </span>
        )}
      </div>
    </div>
  );
}
